import React, { useState, useMemo, useEffect, useRef } from 'react';
import { analyzeSocialMediaLink } from '../services/googleSheets';
import { CONFIG } from '../config/environment';

// Extract YouTube video ID from URL
const extractVideoId = (url: string): string | null => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Generic link preview component that works for all URLs
const LinkPreview: React.FC<{ url: string; videoMetadata?: any }> = ({ url, videoMetadata }) => {
  const [localMetadata, setLocalMetadata] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const videoId = extractVideoId(url);
  
  React.useEffect(() => {
    if (videoId) {
      // Use batch metadata if available, otherwise fallback to individual API call
      if (videoMetadata && videoMetadata[videoId]) {
        setLocalMetadata(videoMetadata[videoId]);
        setLoading(false);
      } else if (!localMetadata) {
        // Fallback to individual API call if batch metadata not available
        fetch(`${CONFIG.API_BASE_URL}/api/youtube/video/${videoId}`)
          .then(res => res.json())
          .then(data => {
            setLocalMetadata(data);
            setLoading(false);
          })
          .catch(err => {
            console.error(`Error fetching video metadata for ${videoId}:`, err);
            setLoading(false);
          });
      }
    } else {
      setLoading(false);
    }
  }, [videoId, videoMetadata, localMetadata]);

  // Parse URL to extract domain and path info
  const parseUrl = (urlString: string) => {
    try {
      const urlObj = new URL(urlString);
      const domain = urlObj.hostname.replace(/^www\./, '');
      const path = urlObj.pathname;
      const pathSegments = path.split('/').filter(segment => segment.length > 0);
      
      return {
        domain,
        path,
        pathSegments,
        protocol: urlObj.protocol
      };
    } catch (error) {
      return {
        domain: 'Unknown',
        path: '',
        pathSegments: [],
        protocol: 'https:'
      };
    }
  };

  const urlInfo = parseUrl(url);
  
  // Get appropriate icon based on domain
  const getDomainIcon = (domain: string) => {
    if (domain.includes('youtube.com') || domain.includes('youtu.be')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      );
    }
    if (domain.includes('twitter.com') || domain.includes('x.com')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      );
    }
    if (domain.includes('instagram.com')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      );
    }
    if (domain.includes('linkedin.com')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      );
    }
    // Default link icon
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M10 6V8H5V19H16V14H18V20A1 1 0 0 1 17 21H4A1 1 0 0 1 3 20V7A1 1 0 0 1 4 6H10ZM21 3V11H19L19 6.413L11.207 14.207L9.793 12.793L17.585 5H13V3H21Z"/>
      </svg>
    );
  };

  // For YouTube URLs, use rich preview
  if (videoId) {
    const metadata = videoMetadata?.[videoId] || localMetadata;
    const thumbnailUrl = metadata?.thumbnail || `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    const title = metadata?.title || `Video ${videoId}`;
    const channelTitle = metadata?.channelTitle || 'YouTube';
    const viewCount = metadata?.viewCount ?
      new Intl.NumberFormat().format(parseInt(metadata.viewCount)) : null;
    const likeCount = metadata?.likeCount ?
      new Intl.NumberFormat().format(parseInt(metadata.likeCount)) : null;
    
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        textDecoration: 'none',
        padding: '0.5rem',
        borderRadius: '8px',
        border: '1px solid #E5E7EB',
        background: 'linear-gradient(135deg, rgba(29, 181, 132, 0.03) 0%, rgba(139, 92, 246, 0.03) 100%)',
        transition: 'all 0.2s',
        maxWidth: '320px',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(29, 181, 132, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)';
        e.currentTarget.style.borderColor = '#1DB584';
        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(29, 181, 132, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(29, 181, 132, 0.03) 0%, rgba(139, 92, 246, 0.03) 100%)';
        e.currentTarget.style.borderColor = '#E5E7EB';
        e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
      }}>
        <img 
          src={thumbnailUrl}
          alt="Video thumbnail"
          style={{
            width: '60px',
            height: '45px',
            borderRadius: '6px',
            objectFit: 'cover',
            flexShrink: 0
          }}
          onError={(e) => {
            e.currentTarget.src = `https://img.youtube.com/vi/${videoId}/default.jpg`;
          }}
        />
        <div style={{ 
          flex: 1, 
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.25rem'
        }}>
          <div style={{
            fontSize: '0.875rem',
            fontWeight: '500',
            color: '#1F2937',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '240px'
          }}
          title={title}>
            {loading ? 'Loading...' : title}
          </div>
          <div style={{
            fontSize: '0.75rem',
            color: '#4B5563',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              {getDomainIcon(urlInfo.domain)}
              {channelTitle}
            </span>
            {viewCount && (
              <span>• 👁️ {viewCount}</span>
            )}
            {likeCount && (
              <span>• 👍 {likeCount}</span>
            )}
          </div>
        </div>
      </a>
    );
  }

  // For non-YouTube URLs, use styled link preview
  const displayTitle = urlInfo.pathSegments.length > 0 
    ? urlInfo.pathSegments[urlInfo.pathSegments.length - 1].replace(/[-_]/g, ' ')
    : urlInfo.domain;
  
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      textDecoration: 'none',
      padding: '0.5rem',
      borderRadius: '8px',
      border: '1px solid #E5E7EB',
      background: 'linear-gradient(135deg, rgba(29, 181, 132, 0.03) 0%, rgba(139, 92, 246, 0.03) 100%)',
      transition: 'all 0.2s',
      maxWidth: '320px',
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(29, 181, 132, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)';
      e.currentTarget.style.borderColor = '#1DB584';
      e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(29, 181, 132, 0.15)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(29, 181, 132, 0.03) 0%, rgba(139, 92, 246, 0.03) 100%)';
      e.currentTarget.style.borderColor = '#E5E7EB';
      e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
    }}>
      <div style={{
        width: '60px',
        height: '45px',
        borderRadius: '6px',
        backgroundColor: '#F3F4F6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: '#4B5563'
      }}>
        {getDomainIcon(urlInfo.domain)}
      </div>
      <div style={{ 
        flex: 1, 
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem'
      }}>
        <div style={{
          fontSize: '0.875rem',
          fontWeight: '500',
          color: '#1F2937',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '240px',
          textTransform: 'capitalize'
        }}
        title={displayTitle}>
          {displayTitle}
        </div>
        <div style={{
          fontSize: '0.75rem',
          color: '#4B5563',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem'
        }}>
          <span style={{ 
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {urlInfo.domain}
          </span>
        </div>
      </div>
    </a>
  );
};

interface FilterBuilderProps {
  headers: string[];
  columnOptions: {[key: string]: string[]};
  onAddFilter: (column: string, value: string) => void;
}

interface AnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  analysis: {content: string, videoId: string, bookingCount: number} | null;
}

const AnalysisModal: React.FC<AnalysisModalProps> = ({ isOpen, onClose, analysis }) => {
  if (!isOpen || !analysis) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '600px',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        position: 'relative'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '16px'
        }}>
          <div>
            <h3 style={{ 
              margin: 0, 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#1a1a1a',
              marginBottom: '4px'
            }}>
              AI Analysis Results
            </h3>
            <p style={{ 
              margin: 0, 
              fontSize: '12px', 
              color: '#6b7280' 
            }}>
              Video ID: {analysis.videoId} • {analysis.bookingCount} booking{analysis.bookingCount > 1 ? 's' : ''} analyzed
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#6b7280',
              cursor: 'pointer',
              padding: '0',
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>
        
        <div style={{
          fontSize: '14px',
          lineHeight: '1.6',
          color: '#374151',
          whiteSpace: 'pre-line'
        }}>
          {analysis.content}
        </div>
      </div>
    </div>
  );
};

const FilterBuilder: React.FC<FilterBuilderProps> = ({ headers, columnOptions, onAddFilter }) => {
  const [selectedColumn, setSelectedColumn] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const handleAddFilter = () => {
    if (selectedColumn === 'DateTime' && (dateRange.start || dateRange.end)) {
      const rangeFilter = `${dateRange.start || ''}|${dateRange.end || ''}`;
      onAddFilter(selectedColumn, rangeFilter);
      setSelectedColumn('');
      setDateRange({ start: '', end: '' });
    } else if (selectedColumn && selectedValue) {
      onAddFilter(selectedColumn, selectedValue);
      setSelectedColumn('');
      setSelectedValue('');
    }
  };

  const availableOptions = selectedColumn ? columnOptions[selectedColumn] || [] : [];
  const isDateColumn = selectedColumn === 'DateTime';
  const canAdd = isDateColumn ? (dateRange.start || dateRange.end) : selectedValue;

  const resetInputs = () => {
    setSelectedValue('');
    setDateRange({ start: '', end: '' });
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      <select
        value={selectedColumn}
        onChange={(e) => {
          setSelectedColumn(e.target.value);
          resetInputs();
        }}
        style={{
          padding: '4px 8px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          fontSize: '12px',
          backgroundColor: 'white',
          cursor: 'pointer'
        }}
      >
        <option value="">Select column...</option>
        {headers.map((header, index) => (
          <option key={index} value={header}>
            {header}
          </option>
        ))}
      </select>
      
      {selectedColumn && !isDateColumn && (
        <>
          <select
            value={selectedValue}
            onChange={(e) => setSelectedValue(e.target.value)}
            style={{
              padding: '4px 8px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '12px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Select value...</option>
            {availableOptions.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        </>
      )}

      {isDateColumn && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#6b7280' }}>From:</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              style={{
                padding: '4px 6px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '11px',
                backgroundColor: 'white'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <label style={{ fontSize: '11px', color: '#6b7280' }}>To:</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              style={{
                padding: '4px 6px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '11px',
                backgroundColor: 'white'
              }}
            />
          </div>
        </>
      )}
      
      {selectedColumn && (
        <button
          onClick={handleAddFilter}
          disabled={!canAdd}
          style={{
            padding: '4px 12px',
            fontSize: '12px',
            color: 'white',
            backgroundColor: canAdd ? '#2563eb' : '#9ca3af',
            border: 'none',
            borderRadius: '4px',
            cursor: canAdd ? 'pointer' : 'not-allowed'
          }}
        >
          Add
        </button>
      )}
    </div>
  );
};


interface SheetsTableProps {
  data: any[][];
  userId: string;
  showAIAnalysis: boolean;
  videoMetadata?: any;
}

type SortDirection = 'asc' | 'desc' | null;

const SheetsTable: React.FC<SheetsTableProps> = ({ data, userId, showAIAnalysis, videoMetadata }) => {
  const [analysisResults, setAnalysisResults] = useState<{[key: string]: string}>({});
  const [loadingAnalysis, setLoadingAnalysis] = useState<{[key: string]: boolean}>({});
  const [activeFilters, setActiveFilters] = useState<{column: string, value: string}[]>([]);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [columnWidths, setColumnWidths] = useState<{[key: string]: number}>({});
  const [isResizing, setIsResizing] = useState<string | null>(null);
  const [analysisModalOpen, setAnalysisModalOpen] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState<{content: string, videoId: string, bookingCount: number} | null>(null);

  const headers = data?.[0] || [];
  const orgIdIndex = headers.findIndex((h: string) => h === 'OrgID');
  
  // Filter out columns we don't want to display
  const hiddenColumns = ['OrgID', 'Email', 'Trackable Link'];
  const visibleHeaders = headers.filter(header => !hiddenColumns.includes(header));
  
  // Get unique values for each column for filter dropdowns
  const columnOptions = useMemo(() => {
    if (!data || data.length === 0) return {};
    
    const dataHeaders = data[0];
    const dataOrgIdIndex = dataHeaders.findIndex((h: string) => h === 'OrgID');
    const userRows = data.slice(1).filter(row => row[dataOrgIdIndex] === userId);
    
    const options: {[key: string]: string[]} = {};
    
    dataHeaders.forEach((header, index) => {
      const uniqueValues = Array.from(new Set(
        userRows
          .map(row => row[index])
          .filter(value => value != null && value !== '')
          .map(value => String(value))
      )).sort();
      options[header] = uniqueValues;
    });
    
    return options;
  }, [data, userId]);

  // Filter and sort rows
  const processedRows = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const dataHeaders = data[0];
    const dataOrgIdIndex = dataHeaders.findIndex((h: string) => h === 'OrgID');
    let filteredRows = data.slice(1).filter(row => {
      return row[dataOrgIdIndex] === userId;
    });
    
    // Apply column filters
    activeFilters.forEach(({column, value}) => {
      if (value.trim()) {
        const columnIndex = dataHeaders.findIndex((h: string) => h === column);
        if (columnIndex !== -1) {
          filteredRows = filteredRows.filter(row => {
            const cellValue = row[columnIndex]?.toString() || '';
            
            // Handle date range filters
            if (column === 'DateTime' && value.includes('|')) {
              const [startDate, endDate] = value.split('|');
              const cellDate = new Date(cellValue);
              
              if (isNaN(cellDate.getTime())) return false;
              
              let matchesRange = true;
              
              if (startDate) {
                const start = new Date(startDate);
                matchesRange = matchesRange && cellDate >= start;
              }
              
              if (endDate) {
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999); // Include the entire end date
                matchesRange = matchesRange && cellDate <= end;
              }
              
              return matchesRange;
            }
            
            // Handle regular exact match filters
            return cellValue === value;
          });
        }
      }
    });
    
    // Apply sorting
    if (sortColumn && sortDirection) {
      const columnIndex = dataHeaders.findIndex((h: string) => h === sortColumn);
      if (columnIndex !== -1) {
        filteredRows.sort((a, b) => {
          let aVal: any = a[columnIndex] || '';
          let bVal: any = b[columnIndex] || '';
          
          // Special handling for dates
          if (sortColumn === 'DateTime') {
            aVal = new Date(aVal).getTime();
            bVal = new Date(bVal).getTime();
          }
          // Special handling for amounts
          else if (sortColumn === 'Close Amount') {
            aVal = parseFloat(aVal.toString().replace(/[$,]/g, '')) || 0;
            bVal = parseFloat(bVal.toString().replace(/[$,]/g, '')) || 0;
          }
          // String comparison for other columns
          else {
            aVal = aVal.toString().toLowerCase();
            bVal = bVal.toString().toLowerCase();
          }
          
          if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
          if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        });
      }
    }
    
    return filteredRows;
  }, [data, userId, activeFilters, sortColumn, sortDirection]);

  // Infinite scroll: render the first 25 rows, grow by 25 each time the user
  // scrolls the sentinel into view. Reset to 25 whenever the underlying
  // filtered/sorted set changes so a refresh starts at the top.
  const PAGE_SIZE = 25;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [data, activeFilters, sortColumn, sortDirection]);

  const visibleRows = useMemo(
    () => processedRows.slice(0, visibleCount),
    [processedRows, visibleCount]
  );

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (visibleCount >= processedRows.length) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          setVisibleCount(c => Math.min(c + PAGE_SIZE, processedRows.length));
        }
      },
      { rootMargin: '200px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [processedRows.length, visibleCount]);

  const handleSort = (columnName: string) => {
    if (sortColumn === columnName) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : sortDirection === 'desc' ? null : 'asc');
      if (sortDirection === 'desc') {
        setSortColumn(null);
      }
    } else {
      setSortColumn(columnName);
      setSortDirection('asc');
    }
  };
  
  const addFilter = (column: string, value: string) => {
    if (column && value) {
      setActiveFilters(prev => {
        // Remove existing filter for this column
        const filtered = prev.filter(f => f.column !== column);
        return [...filtered, { column, value }];
      });
    }
  };

  const removeFilter = (index: number) => {
    setActiveFilters(prev => prev.filter((_, i) => i !== index));
  };

  const toggleRowSelection = (rowIndex: number) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex);
      } else {
        newSet.add(rowIndex);
      }
      return newSet;
    });
  };

  const selectAllRows = () => {
    if (selectedRows.size === processedRows.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(Array.from({ length: processedRows.length }, (_, i) => i)));
    }
  };

  // Early return after all hooks
  if (!data || data.length === 0) return (
    <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
      <p>No data available</p>
    </div>
  );

  const openAnalysisModal = (content: string, videoId: string, bookingCount: number) => {
    setSelectedAnalysis({ content, videoId, bookingCount });
    setAnalysisModalOpen(true);
  };

  const handleAIAnalysis = async (rowIndex: number, socialMediaLink: string) => {
    const videoId = extractVideoId(socialMediaLink);
    const rowKey = `row-${rowIndex}`;
    
    // If we already have analysis for this video, show it immediately
    if (videoId) {
      const existingAnalysis = Object.entries(analysisResults).find(([key, _]) => {
        const existingRowIndex = parseInt(key.replace('row-', ''));
        if (existingRowIndex < processedRows.length) {
          const existingRow = processedRows[existingRowIndex];
          const smLinkIndex = headers.findIndex((h: string) => h === 'SM Link');
          const existingUrl = existingRow[smLinkIndex];
          return extractVideoId(existingUrl) === videoId;
        }
        return false;
      });
      
      if (existingAnalysis) {
        setAnalysisResults(prev => ({ ...prev, [rowKey]: existingAnalysis[1] }));
        return;
      }
    }
    
    setLoadingAnalysis(prev => ({ ...prev, [rowKey]: true }));
    
    try {
      // Collect all rows with the same video ID
      const videoRows = processedRows.filter((row, index) => {
        const smLinkIndex = headers.findIndex((h: string) => h === 'SM Link');
        const rowUrl = row[smLinkIndex];
        return extractVideoId(rowUrl) === videoId;
      });

      // Prepare aggregated data for analysis
      const aggregatedData = {
        videoUrl: socialMediaLink,
        totalBookings: videoRows.length,
        closedDeals: 0,
        totalRevenue: 0,
        bookingDetails: [] as any[]
      };

      const closedIndex = headers.findIndex((h: string) => h === 'Closed');
      const closeAmountIndex = headers.findIndex((h: string) => h === 'Close Amount');
      const firstNameIndex = headers.findIndex((h: string) => h === 'First Name');
      const lastNameIndex = headers.findIndex((h: string) => h === 'Last Name');
      const dateTimeIndex = headers.findIndex((h: string) => h === 'DateTime');

      videoRows.forEach(row => {
        const isClosed = row[closedIndex] === 'Yes';
        const revenue = parseFloat(row[closeAmountIndex]?.toString().replace(/[$,]/g, '') || '0');
        
        if (isClosed) {
          aggregatedData.closedDeals++;
          aggregatedData.totalRevenue += revenue;
        }

        aggregatedData.bookingDetails.push({
          name: `${row[firstNameIndex]} ${row[lastNameIndex]}`.trim(),
          date: row[dateTimeIndex],
          closed: isClosed,
          revenue: revenue
        });
      });

      // Enhanced analysis prompt with aggregated data
      const enhancedPrompt = `
        Analyze this YouTube video's conversion performance based on ${aggregatedData.totalBookings} total bookings:
        
        Video URL: ${socialMediaLink}
        Total Bookings: ${aggregatedData.totalBookings}
        Closed Deals: ${aggregatedData.closedDeals}
        Total Revenue: $${aggregatedData.totalRevenue.toLocaleString()}
        Conversion Rate: ${((aggregatedData.closedDeals / aggregatedData.totalBookings) * 100).toFixed(1)}%
        
        Recent bookings: ${aggregatedData.bookingDetails.slice(0, 5).map(b => 
          `${b.name} (${b.date}) - ${b.closed ? 'Closed: $' + b.revenue : 'Open'}`
        ).join(', ')}
        
        Please provide insights on this video's performance, conversion patterns, and recommendations for optimization.
      `;

      const analysis = await analyzeSocialMediaLink(socialMediaLink, userId, enhancedPrompt);
      
      // Apply the same analysis to all rows with this video ID
      processedRows.forEach((row, index) => {
        const smLinkIndex = headers.findIndex((h: string) => h === 'SM Link');
        const rowUrl = row[smLinkIndex];
        if (extractVideoId(rowUrl) === videoId) {
          const targetRowKey = `row-${index}`;
          setAnalysisResults(prev => ({ ...prev, [targetRowKey]: analysis }));
        }
      });
      
    } catch (error) {
      console.error('Error analyzing social media link:', error);
      setAnalysisResults(prev => ({ ...prev, [rowKey]: 'Error analyzing social media content. Please try again.' }));
    } finally {
      setLoadingAnalysis(prev => ({ ...prev, [rowKey]: false }));
    }
  };

  
  const getSortIcon = (columnName: string) => {
    if (sortColumn !== columnName) return '↕️';
    if (sortDirection === 'asc') return '⬆️';
    if (sortDirection === 'desc') return '⬇️';
    return '↕️';
  };

  const handleMouseDown = (e: React.MouseEvent, columnName: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(columnName);
    
    const startX = e.clientX;
    const startWidth = columnWidths[columnName] || 150;
    
    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.max(80, startWidth + (e.clientX - startX));
      setColumnWidths(prev => ({ ...prev, [columnName]: newWidth }));
    };
    
    const handleMouseUp = () => {
      setIsResizing(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const getColumnWidth = (columnName: string) => {
    if (columnWidths[columnName]) return columnWidths[columnName];
    // Default widths based on column type
    if (columnName === 'SM Link') return 320; // Wider for YouTube preview
    if (columnName === 'Trackable Link') return 250;
    if (columnName === 'DateTime') return 160;
    if (columnName === 'First Name' || columnName === 'Last Name') return 120;
    if (columnName === 'Close Amount') return 120;
    if (columnName === 'Closed') return 80;
    return 150;
  };

  if (processedRows.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        <p>No bookings found for organization: <strong>{userId}</strong></p>
        {activeFilters.length > 0 && (
          <p style={{ fontSize: '14px', marginTop: '8px', color: '#f59e0b' }}>
            Try clearing filters to see more results.
          </p>
        )}
        <p style={{ fontSize: '14px', marginTop: '8px' }}>
          Make sure your Google Sheet contains data with OrgID matching your login email prefix.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Compact Filter Controls */}
      <div style={{ 
        marginBottom: '20px', 
        padding: '12px', 
        backgroundColor: '#f9fafb', 
        borderRadius: '6px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Filters:</span>
          
          <FilterBuilder 
            headers={visibleHeaders}
            columnOptions={columnOptions}
            onAddFilter={addFilter}
          />
          
          {activeFilters.length > 0 && (
            <button
              onClick={() => setActiveFilters([])}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                color: '#dc2626',
                backgroundColor: 'white',
                border: '1px solid #dc2626',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Clear All
            </button>
          )}
        </div>
        
        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {activeFilters.map((filter, index) => {
              let displayValue = filter.value;
              
              // Format date range display
              if (filter.column === 'DateTime' && filter.value.includes('|')) {
                const [startDate, endDate] = filter.value.split('|');
                if (startDate && endDate) {
                  displayValue = `${startDate} to ${endDate}`;
                } else if (startDate) {
                  displayValue = `from ${startDate}`;
                } else if (endDate) {
                  displayValue = `until ${endDate}`;
                }
              }
              
              return (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#dbeafe',
                  border: '1px solid #3b82f6',
                  borderRadius: '16px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  color: '#1e40af'
                }}>
                  <span>{filter.column}: {displayValue}</span>
                  <button
                    onClick={() => removeFilter(index)}
                    style={{
                      marginLeft: '6px',
                      background: 'none',
                      border: 'none',
                      color: '#dc2626',
                      cursor: 'pointer',
                      fontSize: '14px',
                      padding: '0',
                      lineHeight: 1
                    }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Selection Controls */}
        {selectedRows.size > 0 && (
          <div style={{ 
            marginTop: '12px', 
            padding: '8px 12px', 
            backgroundColor: '#dbeafe', 
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '12px'
          }}>
            <span style={{ color: '#1e40af', fontWeight: '600' }}>
              {selectedRows.size} row{selectedRows.size > 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => setSelectedRows(new Set())}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                color: '#dc2626',
                backgroundColor: 'white',
                border: '1px solid #dc2626',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Clear Selection
            </button>
            {/* Add more bulk actions here if needed */}
          </div>
        )}
      </div>
      
      {/* Table Info Bar */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '8px 12px',
        backgroundColor: '#f9fafb',
        borderTop: '1px solid #e5e7eb',
        borderBottom: '1px solid #e5e7eb',
        fontSize: '12px',
        color: '#6b7280'
      }}>
        <div>
          Showing {visibleRows.length} of {processedRows.length} bookings (of {data.slice(1).filter(row => row[orgIdIndex] === userId).length} total)
          {activeFilters.length > 0 && (
            <span style={{ marginLeft: '8px', color: '#f59e0b' }}>
              (filtered)
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          {selectedRows.size > 0 && (
            <span style={{ color: '#2563eb', fontWeight: '500' }}>
              {selectedRows.size} selected
            </span>
          )}
          <span style={{ fontSize: '11px', opacity: 0.7 }}>
            Click rows to select • Click headers to sort • Drag column edges to resize
          </span>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#f9fafb' }}>
            {/* Selection column */}
            <th style={{ 
              padding: '12px 16px', 
              textAlign: 'center',
              borderBottom: '1px solid #e5e7eb',
              width: '50px'
            }}>
              <input
                type="checkbox"
                checked={selectedRows.size === processedRows.length && processedRows.length > 0}
                onChange={selectAllRows}
                style={{ cursor: 'pointer' }}
              />
            </th>
            
{visibleHeaders.map((header, index) => (
              <th 
                key={index} 
                onClick={(e) => {
                  // Don't sort if we're currently resizing
                  if (!isResizing) {
                    handleSort(header);
                  }
                }}
                style={{ 
                  padding: '12px 16px', 
                  textAlign: 'left', 
                  fontSize: '12px', 
                  fontWeight: '500',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  borderBottom: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  userSelect: 'none',
                  position: 'relative',
                  width: getColumnWidth(header),
                  minWidth: '80px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {header}
                  <span style={{ fontSize: '10px', opacity: 0.7 }}>
                    {getSortIcon(header)}
                  </span>
                </div>
                
                {/* Resize handle */}
                <div
                  onMouseDown={(e) => handleMouseDown(e, header)}
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    cursor: 'col-resize',
                    backgroundColor: isResizing === header ? '#3b82f6' : 'transparent',
                    borderRight: '2px solid transparent',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isResizing) {
                      e.currentTarget.style.backgroundColor = '#e5e7eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isResizing) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                />
              </th>
            ))}
            {showAIAnalysis && (
              <th style={{ 
                padding: '12px 16px', 
                textAlign: 'center', 
                fontSize: '12px', 
                fontWeight: '500',
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                borderBottom: '1px solid #e5e7eb'
              }}>
                AI Analysis
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row, rowIndex) => {
            const rowKey = `row-${rowIndex}`;
            const smLinkIndex = headers.findIndex((h: string) => h === 'SM Link');
            const socialMediaLink = showAIAnalysis && smLinkIndex !== -1 ? row[smLinkIndex] : '';
            const closedIndex = headers.findIndex((h: string) => h === 'Closed');
            const isClosedDeal = row[closedIndex] === 'Yes';
            const isSelected = selectedRows.has(rowIndex);
            const isHovered = hoveredRow === rowIndex;
            
            return (
              <tr 
                key={rowIndex}
                onMouseEnter={() => setHoveredRow(rowIndex)}
                onMouseLeave={() => setHoveredRow(null)}
                style={{ 
                  backgroundColor: isSelected 
                    ? '#dbeafe' 
                    : isHovered 
                      ? '#f8fafc' 
                      : isClosedDeal 
                        ? '#f0fdf4' 
                        : 'white',
                  borderBottom: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  ...(isSelected && {
                    boxShadow: 'inset 3px 0 0 #3b82f6'
                  })
                }}
                onClick={(e) => {
                  if (e.target instanceof HTMLElement && !['BUTTON', 'A', 'INPUT'].includes(e.target.tagName)) {
                    toggleRowSelection(rowIndex);
                  }
                }}
              >
                {/* Selection checkbox */}
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleRowSelection(rowIndex)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ cursor: 'pointer' }}
                  />
                </td>
{visibleHeaders.map((header) => {
                  const cellIndex = headers.findIndex(h => h === header);
                  const cell = row[cellIndex];
                  let cellContent: any = cell;
                  let cellStyle: React.CSSProperties = { 
                    padding: '12px 16px', 
                    fontSize: '14px', 
                    color: '#374151',
                    width: getColumnWidth(header),
                    minWidth: '80px',
                    maxWidth: getColumnWidth(header),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  };
                  
                  // Style specific columns
                  if (header === 'Closed') {
                    cellStyle.fontWeight = '600';
                    cellStyle.color = cell === 'Yes' ? '#16a34a' : '#dc2626';
                  } else if (header === 'Close Amount') {
                    cellStyle.fontWeight = '600';
                    // Handle empty, null, or undefined values
                    if (!cell || cell === '' || cell === null || cell === undefined) {
                      cellContent = '$0.00';
                    }
                    // Format as currency if it's a number
                    else if (!isNaN(parseFloat(cell.toString().replace(/[$,]/g, '')))) {
                      const amount = parseFloat(cell.toString().replace(/[$,]/g, ''));
                      cellContent = new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD'
                      }).format(amount);
                      if (amount > 0) {
                        cellStyle.color = '#16a34a';
                      }
                    } 
                    // If it's not a valid number but has content, show as is
                    else if (cell && cell !== '$0' && cell !== '0' && cell !== '') {
                      cellStyle.color = '#16a34a';
                    }
                    // Default fallback to $0.00
                    else {
                      cellContent = '$0.00';
                    }
                  } else if (header === 'SM Link') {
                    // Use link preview component for SM Link
                    cellStyle.whiteSpace = 'normal';
                    cellStyle.padding = '8px';

                    cellContent = <LinkPreview url={cell} videoMetadata={videoMetadata} />;
                  } else if (header === 'Trackable Link') {
                    // Use link preview component for Trackable Link
                    cellStyle.whiteSpace = 'normal';
                    cellStyle.padding = '8px';

                    cellContent = <LinkPreview url={cell} videoMetadata={videoMetadata} />;
                  } else if (header === 'DateTime') {
                    const date = new Date(cell);
                    cellContent = date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                    cellStyle.color = '#6b7280';
                  }
                  
                  return (
                    <td 
                      key={header} 
                      style={cellStyle}
                    >
                      {cellContent}
                    </td>
                  );
                })}
                {showAIAnalysis && (
                  <td style={{ padding: '12px 16px', fontSize: '14px', textAlign: 'center' }}>
                    {!analysisResults[rowKey] && !loadingAnalysis[rowKey] && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAIAnalysis(rowIndex, socialMediaLink);
                        }}
                        style={{
                          padding: '8px',
                          borderRadius: '6px',
                          color: 'white',
                          fontWeight: '500',
                          border: 'none',
                          cursor: 'pointer',
                          backgroundColor: 'rgba(37, 99, 235, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                          transform: isHovered ? 'scale(1.02)' : 'scale(1)',
                          boxShadow: isHovered ? '0 2px 8px rgba(37, 99, 235, 0.3)' : 'none',
                          width: '36px',
                          height: '36px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(29, 78, 216, 0.3)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(37, 99, 235, 0.2)'}
                        title="Analyze Content with AI"
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M6 2L6.5 5.5L10 6L6.5 6.5L6 10L5.5 6.5L2 6L5.5 5.5L6 2Z" fill="#A855F7"/>
                          <path d="M12 8L12.8 12.2L17 13L12.8 13.8L12 18L11.2 13.8L7 13L11.2 12.2L12 8Z" fill="#3B82F6"/>
                          <path d="M18 3L18.5 6L21 6.5L18.5 7L18 10L17.5 7L15 6.5L17.5 6L18 3Z" fill="#EC4899"/>
                        </svg>
                      </button>
                    )}
                    
                    {loadingAnalysis[rowKey] && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#6b7280',
                        fontSize: '12px'
                      }}>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid #e5e7eb',
                          borderTop: '2px solid #3b82f6',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        Analyzing...
                      </div>
                    )}
                    
                    {analysisResults[rowKey] && !loadingAnalysis[rowKey] && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const videoId = extractVideoId(socialMediaLink);
                          const sameVideoCount = processedRows.filter(r => {
                            const smIndex = headers.findIndex(h => h === 'SM Link');
                            return extractVideoId(r[smIndex]) === videoId;
                          }).length;
                          openAnalysisModal(analysisResults[rowKey], videoId || 'Unknown', sameVideoCount);
                        }}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          color: 'white',
                          fontWeight: '500',
                          border: 'none',  
                          cursor: 'pointer',
                          backgroundColor: '#059669',
                          display: 'block',
                          transition: 'all 0.2s ease',
                          transform: isHovered ? 'scale(1.02)' : 'scale(1)',
                          boxShadow: isHovered ? '0 2px 8px rgba(5, 150, 105, 0.3)' : 'none'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#047857'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                      >
                        View Analysis
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        </table>
        {visibleCount < processedRows.length && (
          <div
            ref={sentinelRef}
            style={{
              padding: '16px',
              textAlign: 'center',
              fontSize: 12,
              color: '#6b7280'
            }}
          >
            Loading more…
          </div>
        )}
      </div>

      {/* Analysis Modal */}
      <AnalysisModal
        isOpen={analysisModalOpen}
        onClose={() => setAnalysisModalOpen(false)}
        analysis={selectedAnalysis}
      />
      
      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SheetsTable;